'use client';

import type { z } from 'zod';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RadioGroup } from '@packages/shared/src/ui/RadioGroup/RadioGroup';
import { Radio } from '@packages/shared/src/ui/Radio/Radio';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { Input } from '@packages/shared/src/ui/Input/Input';
import { FormattedMessage, useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { Locale } from '@packages/config';
import { Link } from '@packages/modules/src/Link/Link';
import type {
  AddressBookMutationResponseSuccessFragment,
  CustomerAddressWriteablePayload,
} from '@packages/gql/generated/shopping/graphql';
import React, { useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@packages/shared/src/ui/Popover/Popover';
import type { MessageValue } from 'react-intl';
import { addressValidationSchema, NAME_MAX_LENGTH } from '../AddressValidation/AddressValidation';
import { useValidationHelperText } from '../AddressValidation/useValidationHelperText';

export type ChangeAddressFormData = z.infer<ReturnType<typeof addressValidationSchema>>;

/**
 * CAUTION:
 * This will be developed further for
 * - address edit in account area
 * - checkout address edit
 *
 * Let's keep it generic and reusable!
 */
type ChangeAddressFormProps = {
  submitHandler: SubmitHandler<ChangeAddressFormData>;
  children: (isSubmitting: boolean) => React.ReactNode;
  markFieldsMandatory?: boolean;
  address?: CustomerAddressWriteablePayload;
  inlineErrors: AddressBookMutationResponseSuccessFragment['inlineErrors'];
  isInvoiceAddress?: boolean;
  showPhoneInput?: boolean;
};

const ChangeAddressForm = ({
  submitHandler,
  children,
  markFieldsMandatory = true,
  address,
  inlineErrors,
  isInvoiceAddress,
  showPhoneInput,
}: ChangeAddressFormProps) => {
  const { formatMessage, locale } = useIntl();
  const config = useConfig();

  const addressSchema = useMemo(
    () => addressValidationSchema(markFieldsMandatory),
    [markFieldsMandatory],
  );

  const getHelperText = useValidationHelperText();

  const email = (
    <strong>
      <Link
        href={`mailto:${config.tracking.schemaOrgOrganisation[locale as Locale].contactPoint?.[0]?.email}`}
      >
        {config.tracking.schemaOrgOrganisation[locale as Locale].contactPoint?.[0]?.email}
      </Link>
    </strong>
  );

  // Convert inlineErrors to react-hook-form errors format
  const serverErrors = useMemo(() => {
    if (!inlineErrors) return undefined;

    const errors: Record<string, { type: string; message?: string }> = {};

    Object.entries(inlineErrors).forEach(([fieldName, errorObject]) => {
      if (fieldName !== '__typename' && errorObject && typeof errorObject === 'object') {
        if ('errorTransmitted' in errorObject && errorObject.errorTransmitted) {
          errors[fieldName] = { type: 'manual', message: errorObject.errorTransmitted };
        } else if ('errorInput' in errorObject && errorObject.errorInput) {
          errors[fieldName] = { type: 'manual' };
        }
      }
    });

    return Object.keys(errors).length > 0 ? errors : undefined;
  }, [inlineErrors]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(addressSchema),
    mode: 'all',
    defaultValues: {
      gender: address?.gender || undefined,
      firstName: address?.firstName,
      lastName: address?.lastName,
      street: address?.street,
      houseNumber: address?.houseNumber,
      additional: address?.additional || '',
      zipCode: address?.zipCode,
      city: address?.city,
      primaryPhoneNumber: address?.primaryPhoneNumber || '',
    },
    errors: serverErrors,
  });

  return (
    <div>
      {markFieldsMandatory && (
        <p className="text-size-body-sm mb-1.5">
          {formatMessage({
            id: 'Login.requiredFieldsHint',
            defaultMessage: '* Diese Felder müssen ausgefüllt werden',
          })}
        </p>
      )}
      <form onSubmit={handleSubmit(submitHandler)} className="flex flex-col gap-2">
        <Controller
          control={control}
          name="gender"
          render={({ field }) => (
            <RadioGroup
              {...field}
              label={`${formatMessage({
                id: 'ChangeAddressForm.labels.gender',
                defaultMessage: 'Wie dürfen wir Sie ansprechen?',
              })}${markFieldsMandatory ? ' *' : ''}`}
              id="gender"
              error={!!errors.gender}
              notificationText={getHelperText('gender', errors.gender)}
            >
              <div className="flex gap-1">
                <Radio
                  onChange={field.onChange}
                  checked={field.value === 'f'}
                  label={formatMessage({
                    id: 'ChangeAddressForm.gender.female',
                    defaultMessage: 'Frau',
                  })}
                  value="f"
                  name="gender"
                />
                <Radio
                  onChange={field.onChange}
                  checked={field.value === 'm'}
                  label={formatMessage({
                    id: 'ChangeAddressForm.gender.male',
                    defaultMessage: 'Herr',
                  })}
                  value="m"
                  name="gender"
                />
                <Radio
                  onChange={field.onChange}
                  checked={field.value === 'd'}
                  label={formatMessage({
                    id: 'ChangeAddressForm.gender.neutral',
                    defaultMessage: 'Neutrale Ansprache',
                  })}
                  value="d"
                  name="gender"
                />
              </div>
            </RadioGroup>
          )}
        />
        {isInvoiceAddress && (
          <Notification variant="outline">
            {formatMessage<{ readonly email: MessageValue }>(
              {
                id: 'ChangeAddressForm.nameHint',
                defaultMessage:
                  'Falls sich einmal Ihr Name ändern sollte, teilen Sie dies bitte an {email} mit.',
              },
              { email },
            )}
          </Notification>
        )}
        <div className="flex flex-col gap-2 md:flex-row">
          <Input
            {...register('firstName')}
            error={!!errors.firstName}
            helperText={getHelperText('firstName', errors.firstName)}
            disabled={isInvoiceAddress}
            maxLength={NAME_MAX_LENGTH}
            containerClassName="w-full"
            label={`${formatMessage({
              id: 'ChangeAddressForm.labels.firstname',
              defaultMessage: 'Vorname',
            })}${markFieldsMandatory ? ' *' : ''}`}
          />
          <Input
            {...register('lastName')}
            error={!!errors.lastName}
            helperText={getHelperText('lastName', errors.lastName)}
            disabled={isInvoiceAddress}
            maxLength={NAME_MAX_LENGTH}
            containerClassName="w-full"
            label={`${formatMessage({
              id: 'ChangeAddressForm.labels.lastname',
              defaultMessage: 'Nachname',
            })}${markFieldsMandatory ? ' *' : ''}`}
          />
        </div>
        <div className="flex flex-col gap-2 md:flex-row">
          <Input
            {...register('street')}
            error={!!errors.street}
            helperText={getHelperText('street', errors.street)}
            containerClassName="w-full"
            label={`${formatMessage({
              id: 'ChangeAddressForm.labels.street',
              defaultMessage: 'Straße',
            })}${markFieldsMandatory ? ' *' : ''}`}
          />
          <Input
            {...register('houseNumber')}
            error={!!errors.houseNumber}
            helperText={getHelperText('houseNumber', errors.houseNumber)}
            containerClassName="w-full"
            label={`${formatMessage({
              id: 'ChangeAddressForm.labels.houseNumber',
              defaultMessage: 'Hausnr.',
            })}${markFieldsMandatory ? ' *' : ''}`}
          />
        </div>
        <div className="flex flex-col gap-2 md:flex-row">
          <div className="flex w-full items-center gap-2">
            <Input
              {...register('additional')}
              error={!!errors.additional}
              helperText={getHelperText('additional', errors.additional)}
              containerClassName="w-full"
              label={`${formatMessage({
                id: 'ChangeAddressForm.labels.additional',
                defaultMessage: 'Adresszusatz',
              })} ${formatMessage({
                id: 'ChangeAddressForm.labels.optional',
                defaultMessage: '(optional)',
              })}`}
            />
            <Popover>
              <React.Fragment key=".0">
                <PopoverTrigger
                  aria-label={formatMessage({
                    id: 'ChangeAddressForm.additionalInfoAriaLabel',
                    defaultMessage: 'Mehr Infos',
                  })}
                >
                  <span className="icon-emp-information-circle-filled mt-3 h-3 w-3" />
                </PopoverTrigger>
                <PopoverContent align="end" variant="tipp">
                  <FormattedMessage
                    id="ChangeAddressForm.additionalInfoHint"
                    defaultMessage="z.B. Etage, Hinterhaus, Firmennamen, Abteilungen"
                  />
                </PopoverContent>
              </React.Fragment>
            </Popover>
          </div>
          <div className="hidden md:block md:w-full" />
        </div>
        <div className="flex flex-col gap-2 md:flex-row">
          <Input
            {...register('zipCode')}
            error={!!errors.zipCode}
            helperText={getHelperText('zipCode', errors.zipCode)}
            containerClassName="w-full"
            label={`${formatMessage({
              id: 'ChangeAddressForm.labels.zipCode',
              defaultMessage: 'Postleitzahl',
            })}${markFieldsMandatory ? ' *' : ''}`}
          />
          <Input
            {...register('city')}
            error={!!errors.city}
            helperText={getHelperText('city', errors.city)}
            containerClassName="w-full"
            label={`${formatMessage({
              id: 'ChangeAddressForm.labels.city',
              defaultMessage: 'Ort',
            })}${markFieldsMandatory ? ' *' : ''}`}
          />
        </div>
        {!isInvoiceAddress && showPhoneInput && (
          <div className="border-color-grey relative rounded-sm border border-solid p-1.5 text-center">
            <div className="text-size-body-base mb-1.5 font-bold">
              <FormattedMessage
                id="ChangeAddressForm.phoneHintHeadline"
                defaultMessage="Schnellere Lieferung"
              />
            </div>
            <div className="text-size-body-sm mb-1.5">
              <FormattedMessage
                id="ChangeAddressForm.phoneHintText"
                defaultMessage="Wir möchten deine Bestellung bzw. Lieferung so zügig wie möglich bearbeiten und würden dich bei Rückfragen gerne direkt anrufen. Bitte gib deshalb eine Telefonnummer an, unter der wir dich am besten erreichen können - vielen Dank."
              />
            </div>
            <Input
              {...register('primaryPhoneNumber')}
              error={!!errors.primaryPhoneNumber}
              helperText={getHelperText('primaryPhoneNumber', errors.primaryPhoneNumber)}
              containerClassName="w-full text-left"
              label={formatMessage({
                id: 'ChangeAddressForm.labels.phone',
                defaultMessage: 'Telefonnummer',
              })}
            />
          </div>
        )}
        {children(isSubmitting)}
      </form>
    </div>
  );
};

// eslint-disable-next-line import-x/no-default-export
export default ChangeAddressForm;
